<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="sidebar" :class="{ 'sidebar-collapsed': collapsed }">
    <el-popover v-if="hasPermission('workspace:view')" v-model:visible="workspaceMenuVisible" trigger="click" placement="bottom-start" :width="320" popper-class="video-workspace-popover">
      <template #reference>
        <button type="button" class="workspace-trigger" :aria-expanded="workspaceMenuVisible" aria-label="切换工作空间">
          <el-icon><FolderOpened /></el-icon>
          <span>{{ workspaceList.find(item => item.id === selectedWorkspaceId)?.name || '选择工作空间' }}</span>
          <el-icon class="workspace-trigger-arrow"><ArrowDown /></el-icon>
        </button>
      </template>
      <div class="workspace-menu-heading"><span>工作空间</span><el-button v-if="hasWorkspaceCreatePermission" text class="new-project-button" @click="openNewWorkspaceDialog"><el-icon><Plus /></el-icon>新建</el-button></div>
      <div class="sidebar-content" v-loading="workspaceLoading" @scroll="handleWorkspaceScroll">
        <template v-if="workspaceList.length > 0">
          <div
            v-for="workspace in workspaceList"
            :key="workspace.id"
            :class="['workspace-item', { 'workspace-item-active': selectedWorkspaceId === workspace.id }]"
          >
            <div
              class="workspace-item-inner"
              role="button"
              tabindex="0"
              :aria-pressed="selectedWorkspaceId === workspace.id"
              :title="workspace.name"
              @click="handleWorkspaceClick(workspace)"
              @keydown.enter="handleWorkspaceClick(workspace)"
              @keydown.space.prevent="handleWorkspaceClick(workspace)"
            >
              <el-icon class="workspace-icon">
                <FolderOpened v-if="selectedWorkspaceId === workspace.id" />
                <Folder v-else />
              </el-icon>
              <span class="workspace-item-name" v-if="!collapsed">{{ workspace.name }}</span>
            </div>
            <div class="workspace-actions" v-if="!collapsed">
              <el-button
                v-if="hasWorkspaceEditPermission"
                text
                class="workspace-action-button"
                aria-label="编辑工作空间"
                @click.stop="openWorkspaceDetail(workspace)"
              >
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button
                v-if="hasWorkspaceDeletePermission"
                text
                class="workspace-action-button"
                aria-label="删除工作空间"
                :loading="deletingWorkspace"
                @click.stop="handleDeleteWorkspace(workspace)"
              >
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
            <div class="workspace-actions collapsed" v-else></div>
          </div>
        </template>
        <div v-else class="workspace-empty">
          <p>暂无工作空间</p>
        </div>
      </div>
    </el-popover>
    <slot />
    <div class="topbar-spacer"></div>

    <div class="sidebar-footer">
      <div class="sidebar-user-info-panel">
        <el-dropdown
          trigger="click"
          placement="bottom-end"
          :teleported="false"
          popper-class="sidebar-user-dropdown"
          @visible-change="handleUserDropdownVisibleChange"
          @command="handleUserCommand"
        >
          <div class="sidebar-user-info">
            <el-avatar
              :size="30"
              :src="userAvatarUrl"
              class="user-avatar"
              :class="{
                'user-avatar--clickable': hasUserAvatar,
                'user-avatar--fallback': !hasUserAvatar
              }"
              :style="avatarFallbackStyle"
              @click.stop="openAvatarPreview"
            >
              {{ avatarInitial }}
            </el-avatar>
            <div class="sidebar-user-meta" v-if="!collapsed">
              <div class="user-name-row">
                <span class="user-name">{{ userInfo?.nickname || userInfo?.username || '用户' }}</span>
                <el-icon
                  class="user-refresh-icon"
                  :class="{ 'refreshing': refreshingUserInfo }"
                  @click.stop="handleUserCommand('refresh')"
                  :title="refreshingUserInfo ? '刷新中...' : '刷新用户信息'"
                >
                  <RefreshRight />
                </el-icon>
              </div>
              <span
                class="user-subtitle"
                v-if="userInfo?.generateCount !== undefined"
              >
                剩余
                <span v-if="isLowGenerateCount" class="user-subtitle-low">
                  {{ generateCountDisplay }}
                </span>
                <span v-else>
                  {{ generateCountDisplay }}
                </span>
                次
              </span>
            </div>
            <el-icon class="dropdown-icon" :class="{ 'is-open': userDropdownVisible }">
              <ArrowDown />
            </el-icon>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-if="hasUploadAvatarPermission" command="uploadAvatar" class="sidebar-dropdown-item">
                <el-icon><Picture /></el-icon>
                <span>上传头像</span>
              </el-dropdown-item>
              <el-dropdown-item v-if="hasModifyPasswordPermission" command="changePassword" class="sidebar-dropdown-item">
                <el-icon><Lock /></el-icon>
                <span>修改密码</span>
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided class="sidebar-dropdown-item logout-item">
                <el-icon><SwitchButton /></el-icon>
                <span>退出登录</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <input
          ref="avatarInputRef"
          type="file"
          accept="image/*"
          style="display: none"
          @change="handleAvatarChange"
        />
      </div>
    </div>
  </div>

  <!-- 新建工作空间对话框 -->
  <el-dialog
    v-model="newWorkspaceDialogVisible"
    title="新建工作空间"
    width="520px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    class="new-workspace-dialog"
  >
    <el-form
      ref="newWorkspaceFormRef"
      :model="newWorkspaceForm"
      :rules="newWorkspaceRules"
      label-position="top"
      @submit.prevent
    >
      <el-form-item label="名称" prop="name">
        <el-input
          v-model="newWorkspaceForm.name"
          placeholder="请输入工作空间名称"
          maxlength="50"
          show-word-limit
          clearable
          @keydown.enter.prevent="handleCreateWorkspaceSubmit"
        />
      </el-form-item>
      <el-form-item label="描述" prop="description">
        <el-input
          v-model="newWorkspaceForm.description"
          type="textarea"
          rows="3"
          maxlength="200"
          show-word-limit
          placeholder="请输入描述（可选）"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="newWorkspaceDialogVisible = false">取消</el-button>
        <el-button v-permission="'workspace:create'" type="primary" :loading="creatingWorkspace" @click="handleCreateWorkspaceSubmit">
          {{ creatingWorkspace ? '创建中...' : '创建' }}
        </el-button>
      </div>
    </template>
  </el-dialog>

  <!-- 工作空间详情对话框 -->
  <el-dialog
    v-model="workspaceDetailDialogVisible"
    title="工作空间详情"
    width="420px"
    :close-on-click-modal="false"
    class="workspace-detail-dialog"
  >
    <el-form
      ref="workspaceDetailFormRef"
      :model="workspaceDetailForm"
      :rules="workspaceDetailRules"
      label-width="90px"
      @submit.prevent
    >
      <el-form-item label="名称" prop="name">
        <el-input
          v-model="workspaceDetailForm.name"
          placeholder="请输入工作空间名称"
          maxlength="50"
          show-word-limit
          clearable
        />
      </el-form-item>
      <el-form-item label="描述" prop="description">
        <el-input
          v-model="workspaceDetailForm.description"
          type="textarea"
          rows="3"
          maxlength="200"
          show-word-limit
          placeholder="请输入描述（可选）"
        />
      </el-form-item>
      <el-form-item label="是否默认">
        <el-switch
          v-model="workspaceDetailForm.isDefault"
          :active-value="1"
          :inactive-value="0"
          :disabled="workspaceDetailInitialDefault === 1"
        />
        <el-button
          text
          type="primary"
          class="set-default-button"
          v-if="workspaceDetailInitialDefault !== 1 && workspaceDetailForm.isDefault !== 1"
          v-permission="'workspace:edit'"
          @click.prevent="handleSetDefaultWorkspace(workspaceDetailForm)"
        >
          设为默认
        </el-button>
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="workspaceDetailDialogVisible = false">取消</el-button>
        <el-button v-permission="'workspace:edit'" type="primary" :loading="updatingWorkspace" @click="handleWorkspaceDetailSubmit">
          {{ updatingWorkspace ? '保存中...' : '保存' }}
        </el-button>
      </div>
    </template>
  </el-dialog>

  <!-- 修改密码对话框 -->
  <el-dialog
    v-model="changePasswordDialogVisible"
    title="修改密码"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    class="change-password-dialog"
  >
    <el-form
      ref="changePasswordFormRef"
      :model="changePasswordForm"
      :rules="changePasswordRules"
      label-width="100px"
      label-position="left"
      @submit.prevent
    >
      <el-form-item label="旧密码" prop="oldPassword">
        <el-input
          v-model="changePasswordForm.oldPassword"
          type="password"
          placeholder="请输入旧密码"
          show-password
          clearable
        />
      </el-form-item>
      <el-form-item label="新密码" prop="newPassword">
        <el-input
          v-model="changePasswordForm.newPassword"
          type="password"
          placeholder="请输入新密码"
          show-password
          clearable
        />
      </el-form-item>
      <el-form-item label="确认新密码" prop="confirmPassword">
        <el-input
          v-model="changePasswordForm.confirmPassword"
          type="password"
          placeholder="请再次输入新密码"
          show-password
          clearable
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="changePasswordDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="changingPassword"
          @click="handleChangePasswordSubmit"
        >
          {{ changingPassword ? '提交中...' : '确定' }}
        </el-button>
      </div>
    </template>
  </el-dialog>

  <!-- 头像冰箱贴预览 -->
  <Transition name="magnet-pop">
    <div v-if="avatarPreviewVisible" class="magnet-overlay" @click="avatarPreviewVisible = false">
      <div class="magnet-card" @click.stop>
        <img
          v-if="avatarPreviewUrl"
          :src="avatarPreviewUrl"
          class="magnet-img"
          @click="avatarPreviewVisible = false"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup>
/* eslint-disable vue/multi-word-component-names */
/* global defineExpose */
import { ref, computed, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Plus,
  Folder,
  FolderOpened,
  Delete,
  Edit,
  ArrowDown,
  Picture,
  Lock,
  SwitchButton,
  RefreshRight
} from '@element-plus/icons-vue'
import {
  updatePassword,
  updateAvatar,
  login,
  logout,
  setToken,
  setUserInfo,
  getUserInfo,
  getUserInfoFromApi,
  hasPermission
} from '@/api/auth'
import { getMyWorkspaces, deleteWorkspace, createWorkspace, updateWorkspace } from '@/api/workspace'
import { SELECTED_WORKSPACE_ID_KEY, SELECTED_SCRIPT_KEY } from '@/config/constants'

// eslint-disable-next-line no-undef
const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: null
  },
  clearCacheLoading: {
    type: Boolean,
    default: false
  }
})

// eslint-disable-next-line no-undef
const emit = defineEmits([
  'toggle',
  'user-command',
  'avatar-change',
  'password-changed',
  'workspace-selected',
  'userInfoRefreshed'
])

const avatarInputRef = ref(null)
const uploadingAvatar = ref(false)
const avatarPreviewVisible = ref(false)
const avatarPreviewUrl = ref('')
const refreshingUserInfo = ref(false)
const router = useRouter()
const avatarFallbackColors = [
  'linear-gradient(135deg, #1875F0 0%, #4FB3FF 100%)',
  'linear-gradient(135deg, #12B886 0%, #63E6BE 100%)',
  'linear-gradient(135deg, #F59F00 0%, #FFD43B 100%)',
  'linear-gradient(135deg, #E03131 0%, #FF8787 100%)',
  'linear-gradient(135deg, #7048E8 0%, #B197FC 100%)',
  'linear-gradient(135deg, #0CA678 0%, #94D82D 100%)',
  'linear-gradient(135deg, #D6336C 0%, #F783AC 100%)',
  'linear-gradient(135deg, #1098AD 0%, #66D9E8 100%)'
]
const userAvatarUrl = computed(() => {
  const avatar = props.userInfo?.avatar
  if (avatar === null || avatar === undefined) return ''
  return String(avatar).trim()
})
const hasUserAvatar = computed(() => Boolean(userAvatarUrl.value))
const avatarNameSource = computed(() => {
  return String(props.userInfo?.nickname || props.userInfo?.username || '用户').trim() || '用户'
})
const avatarInitial = computed(() => {
  const firstChar = Array.from(avatarNameSource.value)[0] || '用'
  return /^[a-z]$/i.test(firstChar) ? firstChar.toUpperCase() : firstChar
})
const avatarFallbackStyle = computed(() => {
  if (hasUserAvatar.value) return {}
  const hashSource = `${avatarNameSource.value}${props.userInfo?.userId || ''}`
  const colorIndex = Array.from(hashSource).reduce((sum, char) => sum + char.codePointAt(0), 0) % avatarFallbackColors.length
  return { background: avatarFallbackColors[colorIndex] }
})

// 用户信息下拉菜单展开/收起状态
const userDropdownVisible = ref(false)
const handleUserDropdownVisibleChange = (visible) => {
  userDropdownVisible.value = !!visible
}

// 工作空间列表相关状态
const workspaceMenuVisible = ref(false)
const workspaceList = ref([])
const workspaceLoading = ref(false)
const workspaceLoadingMore = ref(false)
const workspaceTotal = ref(0)
const workspacePage = ref(1)
const workspacePageSize = 20
const selectedWorkspaceId = ref(null)
const deletingWorkspace = ref(false)

const workspaceHasMore = computed(
  () => workspaceList.value.length < (workspaceTotal.value || 0)
)

const emitWorkspaceSelected = (reason = 'loaded') => {
  const current = workspaceList.value.find(w => w.id === selectedWorkspaceId.value)
  emit('workspace-selected', {
    id: current?.id || null,
    systemPrompt: current?.systemPrompt || '',
    reason
  })
}

// 新建工作空间对话框相关状态
const newWorkspaceDialogVisible = ref(false)
const newWorkspaceFormRef = ref(null)
const creatingWorkspace = ref(false)
const newWorkspaceForm = ref({
  name: '',
  description: ''
})

const newWorkspaceRules = {
  name: [
    { required: true, message: '请输入工作空间名称', trigger: 'blur' },
    { min: 2, max: 50, message: '名称长度应为 2-50 个字符', trigger: 'blur' }
  ],
  description: [
    { max: 200, message: '描述不能超过 200 个字符', trigger: 'blur' }
  ]
}

// 工作空间详情对话框状态
const workspaceDetailDialogVisible = ref(false)
const workspaceDetailFormRef = ref(null)
const workspaceDetailForm = ref({
  id: null,
  name: '',
  description: '',
  isDefault: 0
})
const workspaceDetailInitialDefault = ref(0)
const workspaceDetailRules = {
  name: [
    { required: true, message: '请输入工作空间名称', trigger: 'blur' },
    { min: 2, max: 50, message: '名称长度应为 2-50 个字符', trigger: 'blur' }
  ],
  description: [
    { max: 200, message: '描述不能超过 200 个字符', trigger: 'blur' }
  ]
}
const updatingWorkspace = ref(false)

// 修改密码对话框相关状态
const changePasswordDialogVisible = ref(false)
const changePasswordFormRef = ref(null)
const changingPassword = ref(false)
const changePasswordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// 修改密码表单验证规则
const validateConfirmPassword = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请再次输入新密码'))
  } else if (value !== changePasswordForm.value.newPassword) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const changePasswordRules = {
  oldPassword: [
    { required: true, message: '请输入旧密码', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validateConfirmPassword, trigger: 'blur' }
  ]
}

const generateCountDisplay = computed(() => {
  const count = props.userInfo?.generateCount
  if (count === undefined || count === null) {
    return 0
  }
  if (typeof count === 'object') {
    return count.generateCount !== undefined ? count.generateCount : 0
  }
  return typeof count === 'number' ? count : Number(count) || 0
})

const isLowGenerateCount = computed(() => {
  return generateCountDisplay.value < 20
})

// 检查是否有上传头像权限
const hasUploadAvatarPermission = computed(() => {
  return hasPermission('user:upload_avatar')
})

// 检查是否有修改密码权限
const hasModifyPasswordPermission = computed(() => {
  return hasPermission('user:modify_password')
})

const hasWorkspaceCreatePermission = computed(() => hasPermission('workspace:create'))
const hasWorkspaceEditPermission = computed(() => hasPermission('workspace:edit'))
const hasWorkspaceDeletePermission = computed(() => hasPermission('workspace:delete'))

const handleUserCommand = (command) => {
  if (command === 'uploadAvatar') {
    if (avatarInputRef.value) {
      avatarInputRef.value.click()
    }
  } else if (command === 'changePassword') {
    showChangePasswordDialog()
  } else if (command === 'refresh') {
    handleRefreshUserInfo()
  } else if (command === 'logout') {
    handleLogout()
  } else {
    emit('user-command', command)
  }
}

const openAvatarPreview = () => {
  if (!hasUserAvatar.value) return
  const url = userAvatarUrl.value
  if (url && url.startsWith('http')) {
    avatarPreviewUrl.value = url
    avatarPreviewVisible.value = true
  }
}

const openNewWorkspaceDialog = () => {
  try {
    const savedScript = localStorage.getItem(SELECTED_SCRIPT_KEY)
    if (!savedScript) {
      ElMessage.warning('请选择剧本')
      return
    }
    const parsed = JSON.parse(savedScript)
    if (!parsed || parsed.id === undefined || parsed.id === null) {
      ElMessage.warning('请选择剧本')
      return
    }
  } catch (e) {
    ElMessage.warning('请选择剧本')
    return
  }
  
  newWorkspaceForm.value = {
    name: '',
    description: ''
  }
  newWorkspaceDialogVisible.value = true
  nextTick(() => {
    newWorkspaceFormRef.value?.clearValidate()
  })
}

const handleCreateWorkspaceSubmit = () => {
  if (creatingWorkspace.value) return
  if (!newWorkspaceFormRef.value) return
  
  newWorkspaceFormRef.value.validate(async (valid) => {
    if (!valid) return
    creatingWorkspace.value = true
    try {
      const payload = {
        name: newWorkspaceForm.value.name.trim(),
        description: newWorkspaceForm.value.description?.trim() || undefined
      }
      
      try {
        const savedScript = localStorage.getItem(SELECTED_SCRIPT_KEY)
        if (savedScript) {
          const parsed = JSON.parse(savedScript)
          if (parsed && parsed.id !== undefined && parsed.id !== null) {
            payload.scriptId = parsed.id
          }
        }
      } catch (e) {
        // ignore
      }

      const res = await createWorkspace(payload)
      if (res.code === 200 && res.data) {
        ElMessage.success(res.message || '工作空间创建成功')
        newWorkspaceDialogVisible.value = false
        await loadWorkspaceList({
          reloadVideos: true,
          keepSelection: true,
          append: false,
          preferredWorkspaceId: res.data.id
        })
      } else {
        ElMessage.error(res.message || '创建工作空间失败')
      }
    } catch (error) {
      console.error('创建工作空间失败:', error)
      ElMessage.error('创建工作空间失败')
    } finally {
      creatingWorkspace.value = false
    }
  })
}

const loadWorkspaceList = async (options = {}) => {
  try {
    const savedScript = localStorage.getItem(SELECTED_SCRIPT_KEY)
    if (!savedScript) {
      workspaceList.value = []
      selectedWorkspaceId.value = null
      localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
      emitWorkspaceSelected('no-script')
      return
    }
    const parsed = JSON.parse(savedScript)
    if (!parsed || parsed.id === undefined || parsed.id === null) {
      workspaceList.value = []
      selectedWorkspaceId.value = null
      localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
      emitWorkspaceSelected('no-script')
      return
    }
  } catch (e) {
    workspaceList.value = []
    selectedWorkspaceId.value = null
    localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
    emitWorkspaceSelected('no-script')
    return
  }

  const {
    reloadVideos = false,
    keepSelection = true,
    append = false,
    preferredWorkspaceId = null
  } = options

  const previousSelectedId = selectedWorkspaceId.value
  const savedWorkspaceId = localStorage.getItem(SELECTED_WORKSPACE_ID_KEY)

  if (!append) {
    workspacePage.value = 1
    if (!workspaceLoadingMore.value) {
      workspaceLoading.value = true
    }
  } else {
    workspacePage.value += 1
    workspaceLoadingMore.value = true
  }

  try {
    let scriptIdParam = undefined
    try {
      const savedScript = localStorage.getItem(SELECTED_SCRIPT_KEY)
      if (savedScript) {
        const parsed = JSON.parse(savedScript)
        scriptIdParam = parsed?.id || undefined
      }
    } catch (e) {
      // ignore
    }

    const res = await getMyWorkspaces({
      current: workspacePage.value,
      size: workspacePageSize,
      scriptId: scriptIdParam
    })

    if (res.code === 200 && res.data) {
      const list = res.data.workspaces || []
      workspaceTotal.value = res.data.total || 0
      
      if (append) {
        workspaceList.value = [...workspaceList.value, ...list]
      } else {
        workspaceList.value = list
      }

      if (!append) {
        if (workspaceList.value.length > 0) {
          let targetWorkspaceId = null
          
          if (preferredWorkspaceId) {
            const preferred = workspaceList.value.find(w => w.id === Number(preferredWorkspaceId))
            if (preferred) {
              targetWorkspaceId = preferred.id
            }
          }
          
          if (!targetWorkspaceId && savedWorkspaceId) {
            const savedWorkspace = workspaceList.value.find(w => w.id === Number(savedWorkspaceId))
            if (savedWorkspace) {
              targetWorkspaceId = savedWorkspace.id
            }
          }
          
          if (!targetWorkspaceId && keepSelection && previousSelectedId) {
            const exists = workspaceList.value.find(w => w.id === previousSelectedId)
            if (exists) {
              targetWorkspaceId = previousSelectedId
            }
          }
          
          if (!targetWorkspaceId) {
            const defaultWorkspace = workspaceList.value.find(w => w.isDefault === 1)
            targetWorkspaceId = defaultWorkspace?.id || workspaceList.value[0].id
          }
          selectedWorkspaceId.value = targetWorkspaceId
          localStorage.setItem(SELECTED_WORKSPACE_ID_KEY, String(targetWorkspaceId))
          
          if (reloadVideos) {
            emitWorkspaceSelected('reload-after-load')
          } else {
            emitWorkspaceSelected('loaded')
          }
        } else {
          selectedWorkspaceId.value = null
          localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
          if (reloadVideos) {
            emitWorkspaceSelected('reload-after-load-empty')
          } else {
            emitWorkspaceSelected('loaded-empty')
          }
        }
      }
    } else {
      ElMessage.error(res.message || '获取工作空间列表失败')
    }
  } catch (error) {
    console.error('获取工作空间列表失败:', error)
  } finally {
    workspaceLoading.value = false
    workspaceLoadingMore.value = false
  }
}

const clearWorkspaceList = () => {
  workspaceList.value = []
  selectedWorkspaceId.value = null
  localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
  emitWorkspaceSelected('cleared')
}

defineExpose({
  loadWorkspaceList,
  clearWorkspaceList
})

const showChangePasswordDialog = () => {
  changePasswordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  if (changePasswordFormRef.value) {
    changePasswordFormRef.value.clearValidate()
  }
  changePasswordDialogVisible.value = true
}

const handleChangePasswordSubmit = () => {
  if (!changePasswordFormRef.value) {
    return
  }
  changePasswordFormRef.value.validate((valid) => {
    if (valid) {
      handleChangePassword(
        changePasswordForm.value.oldPassword,
        changePasswordForm.value.newPassword
      )
    } else {
      return false
    }
  })
}

const handleChangePassword = async (oldPassword, newPassword) => {
  changingPassword.value = true
  try {
    const response = await updatePassword({
      oldPassword: oldPassword.trim(),
      newPassword: newPassword.trim()
    })
    if (response.code === 200) {
      ElMessage.success(response.message || '密码修改成功')
      changePasswordDialogVisible.value = false
      changePasswordForm.value = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      await autoLoginAfterPasswordChange(newPassword.trim())
      emit('password-changed')
    } else {
      ElMessage.error(response.message || '密码修改失败')
    }
  } catch (error) {
    console.error('修改密码失败:', error)
    ElMessage.error('修改密码失败，请稍后重试')
  } finally {
    changingPassword.value = false
  }
}

const autoLoginAfterPasswordChange = async (newPassword) => {
  const storedUser = getUserInfo()
  const username = props.userInfo?.username || storedUser?.username
  
  if (!username) {
    ElMessage.error('无法获取用户名，请手动登录')
    return
  }
  
  try {
    const response = await login({
      username,
      password: newPassword
    })
    
    if (response.code === 200 && response.data) {
      if (response.data.token) {
        setToken(response.data.token)
      }
      
      const updatedUserInfo = {
        userId: response.data.userId,
        username: response.data.username,
        nickname: response.data.nickname,
        avatar: response.data.avatar,
        generateCount:
          response.data.generateCount !== undefined
            ? response.data.generateCount
            : props.userInfo?.generateCount || 0
      }
      
      setUserInfo(updatedUserInfo)
      ElMessage.success('登录成功')
      emit('user-command', 'user-info-updated')
    } else {
      ElMessage.error(response.message || '自动登录失败，请手动登录')
    }
  } catch (error) {
    console.error('自动登录失败:', error)
    ElMessage.error('自动登录失败，请手动登录')
  }
}

const handleAvatarChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.error('请选择图片文件')
    event.target.value = ''
    return
  }
  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    ElMessage.error('图片大小不能超过 5MB')
    event.target.value = ''
    return
  }
  uploadingAvatar.value = true
  try {
    const response = await updateAvatar(file)
    if (response.code === 200) {
      if (response.data) {
        const currentUser = getUserInfo() || props.userInfo || {}
        const updatedUserInfo = {
          ...currentUser,
          avatar: response.data
        }
        setUserInfo(updatedUserInfo)
        emit('avatar-change', updatedUserInfo)
      }
      ElMessage.success(response.message || '头像更新成功')
    } else {
      ElMessage.error(response.message || '头像更新失败')
    }
  } catch (error) {
    console.error('头像更新失败:', error)
  } finally {
    uploadingAvatar.value = false
    if (event.target) {
      event.target.value = ''
    }
  }
}

const handleWorkspaceScroll = (event) => {
  const target = event?.target
  if (!target) return
  const { scrollTop, scrollHeight, clientHeight } = target
  const nearBottom = scrollTop + clientHeight + 50 >= scrollHeight
  if (nearBottom && workspaceHasMore.value && !workspaceLoadingMore.value) {
    loadWorkspaceList({ append: true, keepSelection: true })
  }
}

const openWorkspaceDetail = (workspace) => {
  if (!workspace) return
  workspaceDetailForm.value = {
    id: workspace.id,
    name: workspace.name,
    description: workspace.description || '',
    isDefault: workspace.isDefault
  }
  workspaceDetailInitialDefault.value = workspace.isDefault
  workspaceDetailDialogVisible.value = true
  nextTick(() => {
    workspaceDetailFormRef.value?.clearValidate()
  })
}

const handleWorkspaceDetailSubmit = () => {
  if (!workspaceDetailFormRef.value) return
  workspaceDetailFormRef.value.validate(async (valid) => {
    if (!valid) return
    updatingWorkspace.value = true
    try {
      const payload = {
        name: workspaceDetailForm.value.name.trim(),
        description: workspaceDetailForm.value.description?.trim() || undefined,
        isDefault: workspaceDetailForm.value.isDefault
      }
      const res = await updateWorkspace(workspaceDetailForm.value.id, payload)
      if (res.code === 200) {
        ElMessage.success(res.message || '工作空间更新成功')
        workspaceDetailDialogVisible.value = false
        const index = workspaceList.value.findIndex(
          w => w.id === workspaceDetailForm.value.id
        )
        if (index !== -1) {
          workspaceList.value[index] = {
            ...workspaceList.value[index],
            name: payload.name,
            description: payload.description || '',
            isDefault: payload.isDefault
          }
        }
        if (selectedWorkspaceId.value === workspaceDetailForm.value.id) {
          emitWorkspaceSelected(payload.isDefault === 1 ? 'set-default-and-update' : 'update')
        }
      } else {
        ElMessage.error(res.message || '更新工作空间失败')
      }
    } catch (error) {
      console.error('更新工作空间失败:', error)
      ElMessage.error('更新工作空间失败')
    } finally {
      updatingWorkspace.value = false
    }
  })
}

const handleSetDefaultWorkspace = async (workspace) => {
  if (!workspace || workspace.isDefault === 1) {
    ElMessage.info('该工作空间已是默认空间')
    return
  }
  try {
    const res = await updateWorkspace(workspace.id, { isDefault: 1 })
    if (res.code === 200) {
      ElMessage.success(res.message || '已设为默认空间')
      workspaceDetailForm.value.isDefault = 1
      workspaceList.value = workspaceList.value.map(w => ({
        ...w,
        isDefault: w.id === workspace.id ? 1 : 0
      }))
      if (selectedWorkspaceId.value === workspace.id) {
        emitWorkspaceSelected('set-default')
      }
    } else {
      ElMessage.error(res.message || '设置默认空间失败')
    }
  } catch (error) {
    console.error('设置默认空间失败:', error)
    ElMessage.error('设置默认空间失败')
  }
}

const handleWorkspaceClick = (workspace) => {
  if (!workspace) return
  workspaceMenuVisible.value = false
  selectedWorkspaceId.value = workspace.id
  localStorage.setItem(SELECTED_WORKSPACE_ID_KEY, String(workspace.id))
  emitWorkspaceSelected('click')
}

const handleDeleteWorkspace = async (workspace) => {
  if (!workspace) return
  if (workspaceList.value.length <= 1) {
    ElMessage.warning('不能删除最后一个工作空间')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定要删除工作空间「${workspace.name}」吗？此操作不可恢复。`,
      '删除确认',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
  } catch (err) {
    return
  }
  deletingWorkspace.value = true
  try {
    const res = await deleteWorkspace(workspace.id)
    if (res.code === 200) {
      ElMessage.success(res.message || '删除成功')
      const keepSelection = workspace.id !== selectedWorkspaceId.value
      await loadWorkspaceList({ reloadVideos: true, keepSelection })
    } else {
      ElMessage.error(res.message || '删除失败')
    }
  } catch (error) {
    console.error('删除工作空间失败:', error)
  } finally {
    deletingWorkspace.value = false
  }
}

const handleRefreshUserInfo = async () => {
  if (refreshingUserInfo.value) return

  const currentUser = getUserInfo() || props.userInfo
  if (!currentUser?.userId) {
    ElMessage.error('无法获取用户ID')
    return
  }

  refreshingUserInfo.value = true
  try {
    const response = await getUserInfoFromApi(currentUser.userId)
    if (response.code === 200 && response.data) {
      const updatedUserInfo = {
        userId: response.data.id,
        username: response.data.username,
        nickname: response.data.nickname,
        avatar: response.data.avatar,
        generateCount: response.data.generateCount,
        email: response.data.email,
        phone: response.data.phone,
        status: response.data.status,
        lastLoginTime: response.data.lastLoginTime,
        lastLoginIp: response.data.lastLoginIp,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt
      }

      setUserInfo(updatedUserInfo)
      emit('user-command', 'user-info-refreshed')
      emit('user-info-refreshed', updatedUserInfo)
    } else {
      ElMessage.error(response.message || '获取用户信息失败')
    }
  } catch (error) {
    console.error('刷新用户信息失败:', error)
    ElMessage.error('刷新用户信息失败，请稍后重试')
  } finally {
    refreshingUserInfo.value = false
  }
}

const handleLogout = () => {
  ElMessageBox.confirm(
    '确定要退出登录吗？',
    '退出确认',
    {
      confirmButtonText: '确定退出',
      cancelButtonText: '取消',
      type: 'warning',
      center: true,
      customClass: 'logout-confirm-dialog'
    }
  )
    .then(() => {
      logout()
      workspaceList.value = []
      selectedWorkspaceId.value = null
      localStorage.removeItem(SELECTED_WORKSPACE_ID_KEY)
      emitWorkspaceSelected('logout')
      ElMessage.success('已退出登录')
      const currentPath = router.currentRoute.value.fullPath
      router.push({ path: '/login', query: { redirect: currentPath } })
    })
    .catch(() => {
    })
}

onMounted(() => {
  loadWorkspaceList({ reloadVideos: true, keepSelection: false })
})

// The owning video page reloads workspaces when the project changes. Returning
// from another tool must preserve the workspace and the accumulated video list.
</script>

<style scoped src="./video-topbar.css"></style>

<style src="./workspace-create-dialog.css"></style>

<style>
.sidebar-user-dropdown {
  margin-bottom: 10px;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(24, 117, 240, 0.08);
  padding: 8px 0;
}

.sidebar-user-dropdown .el-popper__arrow {
  display: none;
}

.sidebar-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
}

.sidebar-dropdown-item .el-icon {
  font-size: 16px;
}

.logout-item {
  color: #f56c6c;
}

.logout-item .el-icon {
  color: #f56c6c;
}

.workspace-detail-dialog :deep(.el-dialog__body) {
  padding: 0 24px 24px;
}

.workspace-detail-dialog .set-default-button {
  margin-left: 12px;
  padding: 0;
}

:deep(.change-password-dialog) {
  border-radius: 20px;
  overflow: hidden;
}

:deep(.change-password-dialog .el-dialog__header) {
  padding: 20px 20px 10px;
  border-bottom: 1px solid #ebeef5;
}

:deep(.change-password-dialog .el-dialog__title) {
  font-size: 20px;
  font-weight: 600;
  color: #1A2B45;
}

:deep(.change-password-dialog .el-dialog__body) {
  padding: 20px;
}

:deep(.change-password-dialog .el-form-item__label) {
  color: #606266;
  font-weight: 500;
}

:deep(.change-password-dialog .el-input__wrapper) {
  border-radius: 8px;
  border-color: #dcdfe6;
  transition: all 0.3s ease;
}

:deep(.change-password-dialog .el-input__wrapper:hover) {
  border-color: #00C6FF;
}

:deep(.change-password-dialog .el-input.is-focus .el-input__wrapper) {
  border-color: #00C6FF;
  box-shadow: 0 0 0 2px rgba(0, 198, 255, 0.1);
}

:deep(.change-password-dialog .dialog-footer) {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 10px;
}

:deep(.change-password-dialog .el-button) {
  border-radius: 8px;
  padding: 10px 20px;
}

:deep(.change-password-dialog .el-button--primary) {
  background: linear-gradient(90deg, #1875F0 0%, #00C6FF 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(24, 117, 240, 0.3);
}

:deep(.change-password-dialog .el-button--primary:hover) {
  background: linear-gradient(90deg, #00C6FF 0%, #1875F0 100%);
  box-shadow: 0 6px 16px rgba(24, 117, 240, 0.4);
}

/* ================== 头像冰箱贴预览 ================== */

.magnet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(6px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.magnet-card {
  background: #fff;
  padding: 10px 10px 32px 10px;
  border-radius: 4px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.12),
    0 12px 40px rgba(0, 0, 0, 0.08);
  transform: rotate(-2deg);
  cursor: pointer;
  transition: transform 0.3s ease;
  max-width: 320px;
  max-height: 320px;
}

.magnet-card:hover {
  transform: rotate(0deg) scale(1.02);
}

.magnet-img {
  display: block;
  max-width: 300px;
  max-height: 300px;
  object-fit: contain;
  border-radius: 2px;
}

/* 冰箱贴弹出动效 */
.magnet-pop-enter-active {
  transition: opacity 0.2s ease;
}
.magnet-pop-enter-active .magnet-card {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.magnet-pop-leave-active {
  transition: opacity 0.15s ease;
}
.magnet-pop-leave-active .magnet-card {
  transition: transform 0.15s ease;
}
.magnet-pop-enter-from {
  opacity: 0;
}
.magnet-pop-enter-from .magnet-card {
  transform: rotate(-2deg) scale(0.6);
}
.magnet-pop-leave-to {
  opacity: 0;
}
.magnet-pop-leave-to .magnet-card {
  transform: rotate(-2deg) scale(0.8);
}
</style>
